import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';

const ThemeContext = createContext({
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Helper to get system preference
const getSystemTheme = () => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

export function ThemeProvider({ children, defaultTheme = 'system', storageKey = 'axion-theme' }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey);
      if (stored && ['light', 'dark', 'system'].includes(stored)) {
        return stored;
      }
    }
    return defaultTheme;
  });

  // Resolved theme is the actual theme being applied (light or dark)
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    const initial = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : defaultTheme;
    return initial === 'system' ? getSystemTheme() : (initial || 'light');
  });

  // Apply theme to DOM
  const applyTheme = useCallback((themeValue) => {
    const root = window.document.documentElement;
    const resolved = themeValue === 'system' ? getSystemTheme() : themeValue;

    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    setResolvedTheme(resolved);
  }, []);

  // Set theme and persist
  const setTheme = useCallback(async (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem(storageKey, newTheme);
    applyTheme(newTheme);

    // Persist to backend if authenticated
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await api.put('/users/theme', { theme: newTheme });
      } catch (error) {
        console.warn('Failed to persist theme to backend:', error);
      }
    }
  }, [applyTheme, storageKey]);

  const toggleTheme = useCallback(() => {
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  }, [resolvedTheme, setTheme]);

  // Apply theme on mount and theme change
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Listen for system theme changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      setResolvedTheme(e.matches ? 'dark' : 'light');
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  // Load theme from backend on auth
  useEffect(() => {
    const loadThemeFromBackend = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await api.get('/users/theme');
        const backendTheme = response.data.theme;
        if (backendTheme && ['light', 'dark', 'system'].includes(backendTheme)) {
          setThemeState(backendTheme);
          localStorage.setItem(storageKey, backendTheme);
          applyTheme(backendTheme);
        }
      } catch (error) {
        // Ignore - use local storage value
      }
    };

    loadThemeFromBackend();
  }, [applyTheme, storageKey]);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
